/**
 * onestop-seed — what a brand-new One-Stop page contains.
 *
 * THE SKELETON IS A FORM TO COMPLETE, NOT A PAGE THAT LOOKS FINISHED.
 * Seven blocks in the order the Lakeview page uses them, every scope empty and
 * every list block rendering its real empty state. An author lands on the shape
 * of a One-Stop page and fills in which desks, services and events it offers —
 * which is the actual work, and the thing a pre-populated demo page would hide.
 *
 * WHAT IS SEEDED AND WHAT IS DELIBERATELY LEFT UNSET
 * Only the config the registry marks REQUIRED is written, because a block missing
 * a required key refuses the publish and an author would meet that refusal at the
 * end instead of the start. Everything optional is absent, which means the
 * renderer's own localised chrome shows through (`strings.ts`), rather than
 * English section headings baked into a document that declares `supportedLocales`.
 * Authored config always wins over the renderer's strings, so seeding a title
 * here would quietly opt the page out of its own localisation.
 *
 * The three blocks with required keys:
 *   context-banner  text · activeFrom · activeTo
 *   identity-panel  signedOut.title · signedOut.body
 *   help-contact    linkGroups[] (one group, titled, with no links yet)
 *
 * Their seeded copy is AUTHOR-FACING scaffolding — it says what to do with the
 * block. It is not tenant content, and it is not pretending to be: a banner that
 * shipped reading "Move-in week — extra desks are open" would be fiction about a
 * campus this page has never seen.
 */
import {
  emptyOneStopDocument,
  listOneStopPages,
  saveOneStopDraft,
  slugFromTitle,
  uniqueSlug,
  type OneStopBlockInstance,
  type OneStopDocument,
  type OneStopPage,
} from "./onestop-page-api";


/**
 * The blocks a new page starts with, and which of them still need an answer.
 *
 * `needsAttention` is not a validation result — the document is valid and saves
 * cleanly. It is the list the screen turns into "7 things to fill in", so the
 * skeleton reads as a checklist rather than as a finished page an author might
 * publish untouched.
 */
export interface OneStopSkeleton {
  readonly blocks: OneStopBlockInstance[];
  readonly needsAttention: { blockId: string; what: string }[];
}

export function oneStopSkeletonBlocks(_args: { locale: string; timeZone: string }): OneStopSkeleton {
  /* A new One-Stop page starts EMPTY (operator, 2026-09-02).
   *
   * It used to seed seven blocks — banner, search, identity panel, the three
   * lists and help links — on the reasoning that a skeleton is a form to fill
   * in. In practice it was seven things to understand before doing the one thing
   * the page is for, and six of them rendered as empty states that read like
   * faults.
   *
   * The two ways a block arrives now both say what they are doing: ticking a
   * journey in Setup · Configuration adds the block that journey belongs in, and
   * dragging one from the palette puts it where the author dropped it. Neither
   * needs a starting guess.
   *
   * The signature and return shape are unchanged — `needsAttention` is read by
   * the designer's "still to fill in" list, and the argument is kept so seeding
   * can come back without touching every caller. */
  return { blocks: [], needsAttention: [] };
}

/** A whole new One-Stop page: the document, its slug, and the checklist. */
export function newOneStopPage(args: {
  title: string;
  timeZone: string;
  locale?: string;
  supportedLocales?: string[];
  subtitle?: string | null;
}): { slug: string; doc: OneStopDocument; needsAttention: { blockId: string; what: string }[] } {
  const base = emptyOneStopDocument(args);
  const skeleton = oneStopSkeletonBlocks({ locale: base.settings.locale, timeZone: base.settings.timeZone });
  return {
    slug: slugFromTitle(args.title),
    doc: { ...base, blocks: skeleton.blocks },
    needsAttention: skeleton.needsAttention,
  };
}

/**
 * Mint a One-Stop page and SAVE it, in the one act the picker performs.
 *
 * Saved immediately rather than held in memory until the first edit, because the
 * slug is the page's identity and a page that exists only in a tab is a page an
 * accidental reload loses. `PUT` writes a draft and changes nothing a student
 * sees, so there is no cost to existing early.
 *
 * The list read before the save is what makes the slug unique. It is one call,
 * and it is the only way to know what the tenant already holds.
 */
export async function createOneStopPage(args: {
  title: string;
  timeZone: string;
  locale?: string;
  supportedLocales?: string[];
  subtitle?: string | null;
}): Promise<{ page: OneStopPage; needsAttention: { blockId: string; what: string }[] }> {
  const minted = newOneStopPage(args);
  let slug = minted.slug;
  try {
    const existing = await listOneStopPages();
    slug = uniqueSlug(minted.slug, existing.map((row) => row.slug));
  } catch {
    /* The list is an optimisation, not a precondition: if it fails the save may
       still succeed, and a 409 is the honest answer if it does not. Nothing is
       invented to cover for it. */
  }
  const page = await saveOneStopDraft(slug, minted.doc);
  return { page, needsAttention: minted.needsAttention };
}
